import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { Loader2, Search, Save } from 'lucide-react';
import { GlobalContext } from '../context/GlobalContext';
import useApi from '../hooks/useApi';
import repVisitLogsApi from '../lib/repVisitLogsApi';
import { errorToast, successToast } from '../lib/helpers';

const RepVisitLogs = () => {
  const { uinfo } = useContext(GlobalContext);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const minSearchLength = 2;
  const [formData, setFormData] = useState({
    reason: '',
    visitNotes: '',
    outcome: ''
  });

  useEffect(() => {
    document.title = 'Rep Visit Log - Sales Assist';
    return () => {
      document.title = 'Sales Assist';
    };
  }, []);

  const { data: customers, isLoading: loadingCustomers } = useApi({
    url: '/salesassist/cplusassist/getCustomers',
    initial: []
  });

  const customerList = useMemo(() => {
    if (Array.isArray(customers)) return customers;
    return customers?.data || [];
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    const search = customerSearch.toLowerCase();
    if (!search) return customerList;
    return customerList.filter(customer => {
      const keyCode = customer?.KeyCode?.toLowerCase() || '';
      const name = customer?.Name?.toLowerCase() || '';
      return keyCode.includes(search) || name.includes(search);
    });
  }, [customerList, customerSearch]);

  const displayedCustomers = useMemo(() => {
    if (customerSearch.trim().length < minSearchLength) return [];
    return filteredCustomers.slice(0, 50);
  }, [customerSearch, filteredCustomers, minSearchLength]);

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await repVisitLogsApi.get('/api/rep-visit-logs');
      setLogs(response.data || []);
    } catch (error) {
      errorToast(error.message || 'Failed to load visit logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleSave = async () => {
    if (!selectedCustomer) {
      errorToast('Please select a customer');
      return;
    }
    if (!formData.reason || !formData.visitNotes || !formData.outcome) {
      errorToast('Please complete all fields');
      return;
    }

    const createdBy = uinfo?.Username || uinfo?.Inits || 'unknown';
    const customerBranch = selectedCustomer?.Branch || selectedCustomer?.MamCode || selectedCustomer?.BranchCode || null;
    const customerPostcode = selectedCustomer?.PCode || selectedCustomer?.Postcode || selectedCustomer?.PostCode || null;

    setIsSaving(true);
    try {
      await repVisitLogsApi.post('/api/rep-visit-logs', {
        customer_keycode: selectedCustomer?.KeyCode,
        customer_name: selectedCustomer?.Name,
        customer_postcode: customerPostcode,
        customer_branch: customerBranch,
        reason: formData.reason,
        visit_notes: formData.visitNotes,
        outcome: formData.outcome,
        created_by: createdBy
      });

      successToast('Visit log saved');
      setFormData({ reason: '', visitNotes: '', outcome: '' });
      setSelectedCustomer(null);
      setCustomerSearch('');
      fetchLogs();
    } catch (error) {
      errorToast(error.message || 'Failed to save visit log');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 relative z-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rep Visit Log</h1>
          <p className="text-sm text-gray-600 mt-1">Create an audit record for customer visits with no follow-up required.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <Combobox value={selectedCustomer} onChange={setSelectedCustomer}>
              <div className="relative">
                <Combobox.Input
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  displayValue={(customer) => customer ? `${customer.KeyCode} - ${customer.Name}` : ''}
                  onChange={(event) => setCustomerSearch(event.target.value)}
                  placeholder="Search by customer name or code..."
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                  <Search className="h-4 w-4" />
                </div>
                <Combobox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg">
                  {loadingCustomers && (
                    <div className="flex items-center justify-center px-3 py-2 text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading customers...
                    </div>
                  )}
                  {!loadingCustomers && customerSearch.trim().length < minSearchLength && (
                    <div className="px-3 py-2 text-gray-500">Type at least {minSearchLength} characters to search</div>
                  )}
                  {!loadingCustomers && customerSearch.trim().length >= minSearchLength && displayedCustomers.length === 0 && (
                    <div className="px-3 py-2 text-gray-500">No customers found</div>
                  )}
                  {!loadingCustomers && displayedCustomers.map((customer) => (
                    <Combobox.Option
                      key={customer.KeyCode}
                      value={customer}
                      className={({ active }) =>
                        `cursor-pointer select-none px-3 py-2 ${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'}`
                      }
                    >
                      <div className="font-medium">{customer.KeyCode}</div>
                      <div className="text-xs text-gray-500">{customer.Name}</div>
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </div>
            </Combobox>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason of visit</label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="e.g. Drop-off, Relationship check-in..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visit notes</label>
            <textarea
              rows={4}
              value={formData.visitNotes}
              onChange={(e) => setFormData(prev => ({ ...prev, visitNotes: e.target.value }))}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Add notes from the visit..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Outcome</label>
            <textarea
              rows={4}
              value={formData.outcome}
              onChange={(e) => setFormData(prev => ({ ...prev, outcome: e.target.value }))}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Capture the outcome or summary..."
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save visit
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent visit logs</h2>
          <p className="text-sm text-gray-500">Latest entries captured for audit purposes.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Outcome</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Notes</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Rep</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loadingLogs && (
                <tr>
                  <td colSpan={6} className="px-6 py-6 text-center text-sm text-gray-500">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading visit logs...
                    </div>
                  </td>
                </tr>
              )}
              {!loadingLogs && logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-6 text-center text-sm text-gray-500">
                    No visit logs yet.
                  </td>
                </tr>
              )}
              {!loadingLogs && logs.map((log) => (
                <tr key={log.id}>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="font-medium">{log.customer_name}</div>
                    <div className="text-xs text-gray-500">{log.customer_keycode}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{log.reason}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{log.outcome}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{log.visit_notes}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{log.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RepVisitLogs;

